import { z } from "zod";

/** Campos que a etiqueta de produção pode mostrar (o código do serviço vai sempre no topo).
 *  Cada campo tem a sua linha; os curtos (cor, prazo, dentes...) vão na coluna da direita. */
export const CAMPOS_DE_PRODUCAO = [
  "paciente",
  "dentista",
  "clinica",
  "servico",
  "material",
  "dentes",
  "cor",
  "prazo",
  "dataDoPedido",
  "observacao",
  "etapa",
  "tecnico",
] as const;
export type CampoDeProducao = (typeof CAMPOS_DE_PRODUCAO)[number];

/** Campos da etiqueta de entrega (destinatário e endereço vão sempre). */
export const CAMPOS_DE_ENTREGA = ["telefone", "remetente", "pedido", "paciente", "dentista", "trabalhos"] as const;
export type CampoDeEntrega = (typeof CAMPOS_DE_ENTREGA)[number];

/** Etiqueta única: campos da parte de produção (o código do serviço vai sempre no topo; serviço e
 *  observação ocupam a largura toda) e da parte de expedição (clínica e endereço, largura toda). */
export const CAMPOS_DA_UNICA_PRODUCAO = ["prazo", "servico", "material", "cor", "dentes", "paciente", "dentista", "clinica", "dataDoPedido", "etapa", "tecnico", "observacao"] as const;
export type CampoDaUnicaProducao = (typeof CAMPOS_DA_UNICA_PRODUCAO)[number];
export const CAMPOS_DA_UNICA_EXPEDICAO = ["clinica", "dentista", "paciente", "endereco", "telefone", "pedido", "prazo"] as const;
export type CampoDaUnicaExpedicao = (typeof CAMPOS_DA_UNICA_EXPEDICAO)[number];

export const ROTULO_DO_CAMPO: Record<CampoDeProducao | CampoDeEntrega | CampoDaUnicaExpedicao, string> = {
  paciente: "Paciente",
  dentista: "Dentista",
  clinica: "Clínica",
  servico: "Serviço",
  material: "Material",
  dentes: "Dentes",
  cor: "Cor",
  prazo: "Prazo",
  dataDoPedido: "Data do pedido",
  observacao: "Observação do serviço",
  etapa: "Etapa atual",
  tecnico: "Técnico responsável",
  telefone: "Telefone do destinatário",
  remetente: "Remetente (laboratório)",
  pedido: "Número do pedido",
  trabalhos: "Códigos dos trabalhos",
  endereco: "Endereço",
};

/** O que vai grande na primeira linha: o código do serviço ou qualquer campo da etiqueta. */
export const DESTAQUES = ["codigo", ...CAMPOS_DE_PRODUCAO] as const;
export const TAMANHOS_DE_ETIQUETA = ["60x40", "50x30", "100x50", "a4"] as const;
export const FONTES_DE_ETIQUETA = ["compacta", "normal", "grande"] as const;

const campo = <T extends string>(chaves: readonly [T, ...T[]]) =>
  z.object({ chave: z.enum(chaves), ligado: z.boolean() });

const base = {
  tamanho: z.enum(TAMANHOS_DE_ETIQUETA),
  fonte: z.enum(FONTES_DE_ETIQUETA),
  logo: z.boolean(),
};

export const etiquetaDeProducaoSchema = z.object({
  ...base,
  campos: z.array(campo(CAMPOS_DE_PRODUCAO)).max(CAMPOS_DE_PRODUCAO.length),
  destaque: z.enum(DESTAQUES),
});
export const etiquetaDeEntregaSchema = z.object({
  ...base,
  campos: z.array(campo(CAMPOS_DE_ENTREGA)).max(CAMPOS_DE_ENTREGA.length),
  /** QR do catálogo público do laboratório (vai com o trabalho até o dentista). Só imprime com o
   *  catálogo público ligado em Configurações › Perfil público. */
  qrCatalogo: z.boolean(),
});
/** Etiqueta única 10 × 15, uma por serviço: produção em cima, expedição (clínica, dentista,
 *  paciente e endereço) embaixo, separadas por uma linha de corte. */
/** Limites do tamanho da etiqueta única, em mm. */
export const LIMITES_DA_UNICA = { largura: [50, 120], altura: [60, 200], expedicao: [0, 80] } as const;

export const etiquetaUnicaSchema = z.object({
  largura: z.number().int().min(LIMITES_DA_UNICA.largura[0]).max(LIMITES_DA_UNICA.largura[1]),
  altura: z.number().int().min(LIMITES_DA_UNICA.altura[0]).max(LIMITES_DA_UNICA.altura[1]),
  /** Altura da parte de expedição (abaixo da linha de corte). 0 = sem expedição. */
  alturaDaExpedicao: z.number().int().min(LIMITES_DA_UNICA.expedicao[0]).max(LIMITES_DA_UNICA.expedicao[1]),
  camposProducao: z.array(campo(CAMPOS_DA_UNICA_PRODUCAO)).max(CAMPOS_DA_UNICA_PRODUCAO.length),
  camposExpedicao: z.array(campo(CAMPOS_DA_UNICA_EXPEDICAO)).max(CAMPOS_DA_UNICA_EXPEDICAO.length),
  fonte: z.enum(FONTES_DE_ETIQUETA),
  logo: z.boolean(),
  /** QR code na parte de produção que abre o pedido no sistema (celular na bancada). */
  qr: z.boolean(),
  /** QR do catálogo público na parte de expedição (a que vai com o trabalho até o dentista). */
  qrCatalogo: z.boolean(),
  /** Abre a impressão das etiquetas assim que o pedido é aceito. */
  imprimirAoAceitar: z.boolean(),
});
export const configDeEtiquetasSchema = z.object({
  producao: etiquetaDeProducaoSchema,
  entrega: etiquetaDeEntregaSchema,
  unica: etiquetaUnicaSchema,
});
export type EtiquetaDeProducao = z.infer<typeof etiquetaDeProducaoSchema>;
export type EtiquetaDeEntrega = z.infer<typeof etiquetaDeEntregaSchema>;
export type EtiquetaUnica = z.infer<typeof etiquetaUnicaSchema>;
export type ConfigDeEtiquetas = z.infer<typeof configDeEtiquetasSchema>;

const ligados = <T extends string>(todos: readonly T[], on: readonly T[]) => [
  ...on.map((chave) => ({ chave, ligado: true })),
  ...todos.filter((c) => !on.includes(c)).map((chave) => ({ chave, ligado: false })),
];

/** A etiqueta de antes da personalização — é o que vale enquanto o laboratório não mexe. */
export const ETIQUETAS_PADRAO: ConfigDeEtiquetas = {
  producao: {
    tamanho: "60x40",
    fonte: "normal",
    logo: false,
    destaque: "codigo",
    campos: ligados(CAMPOS_DE_PRODUCAO, ["paciente", "dentista", "clinica", "servico", "material", "dentes", "cor", "prazo"]),
  },
  entrega: {
    tamanho: "60x40",
    fonte: "normal",
    logo: false,
    campos: ligados(CAMPOS_DE_ENTREGA, ["telefone", "remetente", "pedido", "paciente", "dentista", "trabalhos"]),
    qrCatalogo: false,
  },
  unica: {
    largura: 100,
    altura: 150,
    alturaDaExpedicao: 25,
    camposProducao: ligados(CAMPOS_DA_UNICA_PRODUCAO, ["prazo", "servico", "material", "cor", "dentes", "dataDoPedido", "paciente", "dentista", "observacao"]),
    camposExpedicao: ligados(CAMPOS_DA_UNICA_EXPEDICAO, ["clinica", "dentista", "paciente", "endereco"]),
    fonte: "normal",
    logo: false,
    qr: true,
    qrCatalogo: false,
    imprimirAoAceitar: true,
  },
};

/** Pontos de partida prontos — escolher um preenche a configuração, que ainda dá pra ajustar. */
export const MODELOS_DE_ETIQUETA: { chave: string; nome: string; descricao: string; tipo: "producao" | "entrega"; config: EtiquetaDeProducao | EtiquetaDeEntrega }[] = [
  { chave: "completa", nome: "Completa", descricao: "Tudo o que importa na bancada", tipo: "producao", config: ETIQUETAS_PADRAO.producao },
  {
    chave: "bancada",
    nome: "Bancada",
    descricao: "Serviço, cor e dentes grandes; etapa e técnico",
    tipo: "producao",
    config: { tamanho: "60x40", fonte: "grande", logo: false, destaque: "codigo", campos: ligados(CAMPOS_DE_PRODUCAO, ["servico", "cor", "dentes", "etapa", "tecnico", "prazo"]) },
  },
  {
    chave: "codigo-e-cor",
    nome: "Só código e cor",
    descricao: "Etiqueta pequena pra peça",
    tipo: "producao",
    config: { tamanho: "50x30", fonte: "grande", logo: false, destaque: "cor", campos: ligados(CAMPOS_DE_PRODUCAO, ["paciente", "dentes"]) },
  },
  { chave: "entrega-completa", nome: "Entrega completa", descricao: "Endereço, remetente e trabalhos", tipo: "entrega", config: ETIQUETAS_PADRAO.entrega },
  {
    chave: "entrega-simples",
    nome: "Entrega simples",
    descricao: "Só destinatário, endereço e pedido",
    tipo: "entrega",
    config: { tamanho: "60x40", fonte: "grande", logo: false, campos: ligados(CAMPOS_DE_ENTREGA, ["pedido"]), qrCatalogo: false },
  },
];

/** Completa uma lista de campos salva: tira o desconhecido e o repetido, e acrescenta no fim
 *  (desligado) campo que surgiu depois de o laboratório salvar. */
function completarCampos<T extends string>(salvos: { chave: string; ligado: boolean }[] | undefined, todos: readonly T[], padrao: { chave: T; ligado: boolean }[]) {
  if (!salvos) return padrao;
  const vistos = new Set<string>();
  const validos = salvos.filter((c) => (todos as readonly string[]).includes(c.chave) && !vistos.has(c.chave) && vistos.add(c.chave)) as { chave: T; ligado: boolean }[];
  return [...validos, ...todos.filter((c) => !vistos.has(c)).map((chave) => ({ chave, ligado: false }))];
}

/** A configuração que vale: a salva pelo laboratório, completada com o padrão onde faltar. */
export function resolverEtiquetas(salvo: unknown): ConfigDeEtiquetas {
  const s = (salvo && typeof salvo === "object" ? salvo : {}) as Partial<Record<"producao" | "entrega", Record<string, unknown>>>;
  const p = s.producao ?? {};
  const e = s.entrega ?? {};
  const u = (s as Record<string, Record<string, unknown> | undefined>).unica ?? {};
  const numero = (v: unknown, [min, max]: readonly [number, number], padrao: number) =>
    typeof v === "number" && Number.isInteger(v) && v >= min && v <= max ? v : padrao;
  const escolhe = <T extends string>(v: unknown, opcoes: readonly T[], padrao: T): T => (opcoes.includes(v as T) ? (v as T) : padrao);
  return {
    producao: {
      tamanho: escolhe(p.tamanho, TAMANHOS_DE_ETIQUETA, ETIQUETAS_PADRAO.producao.tamanho),
      fonte: escolhe(p.fonte, FONTES_DE_ETIQUETA, ETIQUETAS_PADRAO.producao.fonte),
      logo: typeof p.logo === "boolean" ? p.logo : false,
      destaque: escolhe(p.destaque, DESTAQUES, "codigo"),
      campos: completarCampos(Array.isArray(p.campos) ? (p.campos as never) : undefined, CAMPOS_DE_PRODUCAO, ETIQUETAS_PADRAO.producao.campos),
    },
    entrega: {
      tamanho: escolhe(e.tamanho, TAMANHOS_DE_ETIQUETA, ETIQUETAS_PADRAO.entrega.tamanho),
      fonte: escolhe(e.fonte, FONTES_DE_ETIQUETA, ETIQUETAS_PADRAO.entrega.fonte),
      logo: typeof e.logo === "boolean" ? e.logo : false,
      campos: completarCampos(Array.isArray(e.campos) ? (e.campos as never) : undefined, CAMPOS_DE_ENTREGA, ETIQUETAS_PADRAO.entrega.campos),
      qrCatalogo: typeof e.qrCatalogo === "boolean" ? e.qrCatalogo : false,
    },
    unica: {
      largura: numero(u.largura, LIMITES_DA_UNICA.largura, ETIQUETAS_PADRAO.unica.largura),
      altura: numero(u.altura, LIMITES_DA_UNICA.altura, ETIQUETAS_PADRAO.unica.altura),
      alturaDaExpedicao: numero(u.alturaDaExpedicao, LIMITES_DA_UNICA.expedicao, ETIQUETAS_PADRAO.unica.alturaDaExpedicao),
      camposProducao: completarCampos(Array.isArray(u.camposProducao) ? (u.camposProducao as never) : undefined, CAMPOS_DA_UNICA_PRODUCAO, ETIQUETAS_PADRAO.unica.camposProducao),
      camposExpedicao: completarCampos(Array.isArray(u.camposExpedicao) ? (u.camposExpedicao as never) : undefined, CAMPOS_DA_UNICA_EXPEDICAO, ETIQUETAS_PADRAO.unica.camposExpedicao),
      fonte: escolhe(u.fonte, FONTES_DE_ETIQUETA, ETIQUETAS_PADRAO.unica.fonte),
      logo: typeof u.logo === "boolean" ? u.logo : ETIQUETAS_PADRAO.unica.logo,
      qr: typeof u.qr === "boolean" ? u.qr : ETIQUETAS_PADRAO.unica.qr,
      qrCatalogo: typeof u.qrCatalogo === "boolean" ? u.qrCatalogo : false,
      imprimirAoAceitar: typeof u.imprimirAoAceitar === "boolean" ? u.imprimirAoAceitar : ETIQUETAS_PADRAO.unica.imprimirAoAceitar,
    },
  };
}
